import type { ProgramPreparation } from './audition-plan';
import type { FinalLock, ManualChecks, OfficialProof, Program, Take, Verdict } from './model';
import { evaluateTake, overallVerdict } from './preflight';

export interface TakeTimelineItem {
  take: Take;
  order: number;
  verdict: Verdict;
  checks: { pass: number; check: number; fail: number };
  isSelected: boolean;
  selectionReason: string;
}

export interface AuditionMilestone {
  id: 'listing' | 'repertoire' | 'takes' | 'selection' | 'upload' | 'result';
  label: string;
  detail: string;
  state: 'done' | 'pending';
  occurredAt?: string;
}

export interface AuditionTakeTimeline {
  milestones: AuditionMilestone[];
  takes: TakeTimelineItem[];
  comparisonReady: boolean;
}

const auditionStageLabel: Record<ProgramPreparation['outcome']['stage'], string> = {
  planning: '준비 중',
  submitted: '제출 완료',
  callback: '추가 자료 요청',
  live_round: '현장 오디션',
  closed: '지원 종료',
};

const countChecks = (take: Take, program: Program, lock: FinalLock | undefined, manual: ManualChecks) => {
  const results = evaluateTake(program, take, lock, manual);
  return {
    verdict: overallVerdict(results),
    checks: {
      pass: results.filter((item) => item.verdict === 'pass').length,
      check: results.filter((item) => item.verdict === 'check').length,
      fail: results.filter((item) => item.verdict === 'fail').length,
    },
  };
};

export function buildAuditionTakeTimeline(input: {
  program: Program;
  takes: Take[];
  lock?: FinalLock;
  manual: ManualChecks;
  preparation: ProgramPreparation;
  proof?: OfficialProof;
}): AuditionTakeTimeline {
  const { program, lock, manual, preparation, proof } = input;
  const sortedTakes = [...input.takes].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const takeItems = sortedTakes.map((take, index) => {
    const evaluation = countChecks(take, program, lock, manual);
    return {
      take,
      order: index + 1,
      ...evaluation,
      isSelected: lock?.takeId === take.id && lock.hash === take.hash,
      selectionReason: take.note.trim(),
    };
  });
  const repertoire = preparation.repertoire;
  const outcome = preparation.outcome;

  return {
    takes: takeItems,
    comparisonReady: takeItems.length >= 2,
    milestones: [
      {
        id: 'listing', label: '공고·요강',
        detail: program.sourceConfirmedAt ? `${program.sourceRevision} 요강을 직접 확인` : '공식 요강 확인 전',
        state: program.sourceConfirmedAt ? 'done' : 'pending', occurredAt: program.sourceConfirmedAt,
      },
      {
        id: 'repertoire', label: '레퍼토리',
        detail: repertoire.length ? `${repertoire.length}곡의 악장과 촬영 여부를 기록` : '곡과 악장을 먼저 기록하세요',
        state: repertoire.length ? 'done' : 'pending', occurredAt: repertoire.length ? preparation.updatedAt : undefined,
      },
      {
        id: 'takes', label: '연주 영상',
        detail: takeItems.length ? `${takeItems.length}개 영상을 이 기기에 기록` : '연주 영상 없음',
        state: takeItems.length ? 'done' : 'pending', occurredAt: takeItems.at(-1)?.take.createdAt,
      },
      {
        id: 'selection', label: '보낼 영상',
        detail: lock ? '직접 고른 파일의 확인 코드를 저장' : '자동 추천 없이 직접 선택',
        state: lock ? 'done' : 'pending', occurredAt: lock?.lockedAt,
      },
      {
        id: 'upload', label: '제출 완료',
        detail: proof ? '지원처 홈페이지에서 본 완료 내용을 기록' : '지원처의 제출 완료 확인 전',
        state: proof ? 'done' : 'pending', occurredAt: proof?.submittedAt,
      },
      {
        id: 'result', label: '공식 결과',
        detail: outcome.recordedAt ? `${auditionStageLabel[outcome.stage]} · 다음 할 일 기록` : '결과를 예측하지 않고 공식 알림을 기다림',
        state: outcome.recordedAt ? 'done' : 'pending', occurredAt: outcome.recordedAt || undefined,
      },
    ],
  };
}
